import React, { useState, useEffect, useCallback,useRef  } from 'react';

import { Helmet } from 'react-helmet-async';

import { Link } from 'react-router-dom';
import {Clock, Edit2, Trash2, Send, Plus, Eye, X, List as ListIcon, Ticket, ChevronDown, Search } from 'lucide-react';
import EnhancedTemplateModal from './EnhancedTemplateModal';
import ConfigureTimerModal from './ConfigureTimerModal';
import { fetchWithAuth } from '../../utils/api';
import { actionOptions, defaultTemplates } from './templateConfig'; 
import './EmailTemplate.css';

const API_BASE_URL = process.env.REACT_APP_API_URL;

// ====================================================================
// --- 1. NEW: Create the SearchableSelect Component ---
// ====================================================================
const SearchableSelect = ({ options, value, onChange, placeholder }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const wrapperRef = useRef(null);

    // Close dropdown on click outside
    useEffect(() => {
        function handleClickOutside(event) {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setIsOpen(false);
                setSearchTerm('');
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [wrapperRef]);

    const handleSelectOption = (optionValue) => {
        onChange(optionValue);
        setIsOpen(false);
        setSearchTerm('');
    };

    const selectedOption = options.flatMap(g => g.options).find(o => o.value === value);
    const displayLabel = selectedOption ? selectedOption.label : placeholder;

    const filteredOptions = options.map(group => ({
        ...group,
        options: group.options.filter(option =>
            option.label.toLowerCase().includes(searchTerm.toLowerCase())
        )
    })).filter(group => group.options.length > 0);

    return (
        <div className="searchable-select" ref={wrapperRef}>
            <button type="button" className="searchable-select__trigger" onClick={() => setIsOpen(!isOpen)}>
                <span>{displayLabel}</span>
                <ChevronDown size={20} className={`searchable-select__chevron ${isOpen ? 'open' : ''}`} />
            </button>
            {isOpen && (
                <div className="searchable-select__dropdown">
                    <div className="searchable-select__search-wrapper">
                        <Search size={18} className="searchable-select__search-icon" />
                        <input
                            type="text"
                            placeholder="Search lists..."
                            className="searchable-select__search-input"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            autoFocus
                        />
                    </div>
                    <ul className="searchable-select__options-list">
                        {filteredOptions.length > 0 ? (
                            filteredOptions.map((group) => (
                                <React.Fragment key={group.label}>
                                    <li className="searchable-select__group-label">{group.label}</li>
                                    {group.options.map((option) => (
                                        <li
                                            key={option.value}
                                            className={`searchable-select__option ${option.value === value ? 'selected' : ''}`}
                                            onClick={() => handleSelectOption(option.value)}
                                        >
                                            {option.label}
                                        </li>
                                    ))}
                                </React.Fragment>
                            ))
                        ) : (
                            <li className="searchable-select__no-results">No lists found.</li>
                        )}
                    </ul>
                </div>
            )}
        </div>
    );
};



const createDefaultTemplate = (actionValue) => {
    const templateData = defaultTemplates[actionValue];
    if (!templateData) {
        return {
            _id: `default-${actionValue}`, name: 'New Template', subject: 'New Email Subject',
            content: `<p>Click 'Edit' to create content for this template.</p>`, action: actionValue,
            variables: [], isActive: true, isDefault: true,
        };
    }
    return {
        _id: `default-${actionValue}`, name: templateData.name, subject: templateData.subject,
        content: templateData.content, action: actionValue, variables: templateData.variables || [],
        isActive: true, isDefault: true,
    };
};

// This modal is for the top-level "Send Coupon Email" button
const CouponCampaignModal = ({ isOpen, onClose, onSend, templates, coupons }) => {
    const [templateId, setTemplateId] = useState('');
    const [couponId, setCouponId] = useState('');
    const [targetUsers, setTargetUsers] = useState('All Users');

    const campaignTemplates = templates.filter(t => 
        actionOptions.find(opt => opt.value === t.action && opt.canSendManually)
    );

    const handleSend = () => {
        if (!templateId || !couponId) {
            alert('Please select both a template and a coupon.');
            return;
        }
        onSend({ templateId, couponId, targetUsers });
    };
    
    if (!isOpen) return null;

    return (
        <div className="emt-preview-modal-overlay">
            <div className="emt-preview-modal-content" style={{maxWidth: '500px'}}>
                 <div className="emt-preview-modal-header">
                    <h3>Send Coupon Campaign</h3>
                    <button onClick={onClose} className="emt-close-btn"><X size={24} /></button>
                </div>
                <div className="emt-form-container" style={{padding: '1.5rem', display: 'grid', gap: '1rem'}}>
                    <div className="emt-form-group">
                        <label htmlFor="template-select">Email Template</label>
                        <select id="template-select" value={templateId} onChange={(e) => setTemplateId(e.target.value)}>
                            <option value="">-- Choose a template --</option>
                            {campaignTemplates.map(t => <option key={t._id} value={t._id}>{t.name}</option>)}
                        </select>
                    </div>
                    <div className="emt-form-group">
                        <label htmlFor="coupon-select">Coupon to Send</label>
                        <select id="coupon-select" value={couponId} onChange={(e) => setCouponId(e.target.value)}>
                            <option value="">-- Choose a coupon --</option>
                            {(coupons || []).map(c => <option key={c._id} value={c._id}>{c.code} - {c.description}</option>)}
                        </select>
                    </div>
                     <div className="emt-form-group">
                        <label htmlFor="target-users-select">Target Audience</label>
                        <select id="target-users-select" value={targetUsers} onChange={(e) => setTargetUsers(e.target.value)}>
                            <option>All Users</option>
                            <option>Paid Users</option>
                        </select>
                    </div>
                </div>
                <div className="emt-preview-modal-footer" style={{borderTop: '1px solid #e5e7eb', padding: '1rem 1.5rem', display: 'flex', justifyContent:'flex-end', gap: '0.5rem'}}>
                    <button className="action-btn" onClick={onClose}>Cancel</button>
                    <button className="action-btn-primary" onClick={handleSend}><Send size={16}/> Send Campaign</button>
                </div>
            </div>
        </div>
    );
};

// ====================================================================
// --- 2. MODIFICATION: Update SendCampaignModal ---
// ====================================================================

const SendCampaignModal = ({ isOpen, onClose, onSend, coupons , emailLists  }) => {
    const [couponId, setCouponId] = useState('');
    const [targetUsers, setTargetUsers] = useState('All Users');

     // --- NEW: Reset state when the modal opens for a clean slate ---
    useEffect(() => {
        if (isOpen) {
            setCouponId('');
            setTargetUsers('All Users');
        }
    }, [isOpen]);

    const handleSendClick = () => {
        if (!couponId) {
            alert('Please select a coupon to send with this template.');
            return;
        }
        onSend({ couponId, targetUsers });
        onClose(); // Close modal on send
    };

     // Prepare options for our new searchable component
    const audienceOptions = [
        {
            label: 'Default Groups',
            options: [
                { value: 'All Users', label: 'All Users' },
                { value: 'Paid Users', label: 'Paid Users' },
            ],
        },
        {
            label: 'Custom Lists',
            options: (emailLists || []).map(list => ({
                value: list._id,
                label: `${list.name} (${list.totalUsers} users)`
            })),
        }
    ];
    
    if (!isOpen) return null;

    return (
        <div className="emt-preview-modal-overlay">
            <div className="emt-preview-modal-content" style={{maxWidth: '500px'}}>
                 <div className="emt-preview-modal-header">
                    <h3>Prepare Your Campaign</h3>
                    <button onClick={onClose} className="emt-close-btn"><X size={24} /></button>
                </div>
                <div className="emt-form-container" style={{padding: '1.5rem', display: 'grid', gap: '1rem'}}>
                    {/* Coupon selection remains the same */}
                    <div className="emt-form-group">
                        <label htmlFor="send-coupon-select">Select Coupon</label>
                        <select id="send-coupon-select" value={couponId} onChange={(e) => setCouponId(e.target.value)} className="emt-input">
                            <option value="">-- Choose a coupon for this campaign --</option>
                            {(coupons || []).map(c => <option key={c._id} value={c._id}>{c.code} - {c.description}</option>)}
                        </select>
                    </div>

                    {/* Target Audience is now the new searchable component */}
                     <div className="emt-form-group">
                        <label>Target Audience</label>
                        <SearchableSelect
                            options={audienceOptions}
                            value={targetUsers}
                            onChange={(value) => setTargetUsers(value)}
                            placeholder="-- Select an Audience --"
                        />
                    </div>
                </div>
                <div className="emt-preview-modal-footer" style={{borderTop: '1px solid #e5e7eb', padding: '1rem 1.5rem', display: 'flex', justifyContent:'flex-end', gap: '0.5rem'}}>
                    <button type="button" className="action-btn" onClick={onClose}>Cancel</button>
                    <button type="button" className="action-btn-primary" onClick={handleSendClick}><Send size={16}/> Send Now</button>
                </div>
            </div>
        </div>
    );
};

const EmailTemplate = () => {
    // --- STATE MANAGEMENT ---
    const [templates, setTemplates] = useState([]);
    const [availableCoupons, setAvailableCoupons] = useState([]);
     const [emailLists, setEmailLists] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedTemplateType, setSelectedTemplateType] = useState(actionOptions[0]?.value);

    // Modals
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [editingTemplate, setEditingTemplate] = useState(null);
    const [isTimerModalOpen, setIsTimerModalOpen] = useState(false);
    const [timerConfig, setTimerConfig] = useState(null);
    const [previewingTemplate, setPreviewingTemplate] = useState(null);
    const [isCouponModalOpen, setIsCouponModalOpen] = useState(false);

    // --- NEW: State for the new sending modal ---
    const [isSendModalOpen, setIsSendModalOpen] = useState(false);
    const [sendingContext, setSendingContext] = useState(null); // Will hold the templateId

    // --- API & DATA HANDLING ---
    const fetchData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
              const [templatesData, couponsData, listsData] = await Promise.all([
            fetchWithAuth(`${API_BASE_URL}/api/email-templates`),
            fetchWithAuth(`${API_BASE_URL}/api/coupons`),
            fetchWithAuth(`${API_BASE_URL}/api/lists`) // This line was missing
        ]);

        setTemplates(templatesData);
        setAvailableCoupons(couponsData);
        setEmailLists(listsData); // Now this line will work correctly
        
    } catch (e) {
        console.error("Failed to fetch page data:", e);
        setError(e.message || "Could not load page data.");
    } finally {
        setLoading(false);
    }
}, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleSaveTemplate = async (templateData) => {
        setLoading(true);
        try {
            const payload = { ...templateData };
            if (payload._id && payload._id.startsWith('default-')) {
                delete payload._id;
            }
            const result = await fetchWithAuth(`${API_BASE_URL}/api/email-templates`, {
                method: 'POST',
                body: JSON.stringify(payload)
            });
            alert(result.message || 'Template saved successfully!');
            await fetchData();
            setIsEditModalOpen(false);
            setEditingTemplate(null);
        } catch (error) {
            console.error('❌ Error saving template:', error);
            alert(`Failed to save template: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteTemplate = async (templateId) => {
        if (!window.confirm('Are you sure you want to delete this template?')) return;
        setLoading(true);
        try {
            await fetchWithAuth(`${API_BASE_URL}/api/email-templates/${templateId}`, { method: 'DELETE' });
            alert('Template deleted successfully.');
            await fetchData();
        } catch (e) {
            alert(`Error deleting template: ${e.message}`);
        } finally {
            setLoading(false);
        }
    };

     const handleSendCampaign = async (campaignData) => {
        const templateName = templates.find(t => t._id === campaignData.templateId)?.name || 'the selected template';
        const confirmationMessage = `Are you sure you want to send the campaign "${templateName}" to "${campaignData.targetUsers}" immediately?`;

        if (!window.confirm(confirmationMessage)) return;

        try {
            const result = await fetchWithAuth(`${API_BASE_URL}/api/email-templates/send-campaign`, {
                method: 'POST',
                body: JSON.stringify({ ...campaignData, timeInterval: 'Now' }),
            });
            alert(result.message);
            setIsCouponModalOpen(false);
            setIsSendModalOpen(false); // Close the new modal as well
        } catch (err) {
            console.error(`Failed to send campaign:`, err);
            alert(`Error: ${err.message}`);
        }
    };

    // --- MODAL & ACTION HANDLERS ---
    const handleEditClick = (template) => { setEditingTemplate(template); setIsEditModalOpen(true); };
    const handleAddNewClick = () => {
        setEditingTemplate({
            action: selectedTemplateType, name: '', subject: '', content: '<p>Start building your new template!</p>',
            variables: [], isActive: true,
        });
        setIsEditModalOpen(true);
    };

    // --- MODIFIED: This function now OPENS THE MODAL for coupon templates ---
    const handleManualSendClick = (template) => {
        if (!template._id || template._id.startsWith('default-')) {
            alert("Please save the template before sending.");
            return;
        }

        // Only open the coupon selection modal for templates designated as coupon types
        if (template.action === 'new_coupon' || template.action === 'custom_campaign') {
            setSendingContext({ templateId: template._id });
            setIsSendModalOpen(true);
        } else {
            // For other campaign types that don't need a coupon, send directly
            handleSendCampaign({
                templateId: template._id,
                targetUsers: template.targetUsers || 'All Users',
            });
        }
    };
    
    // --- NEW: This handler is called when the user confirms sending from our new modal ---
    const handleConfirmAndSend = (sendData) => {
        // Combine the templateId from context with the couponId and targetUsers from the modal
        const finalPayload = {
            templateId: sendingContext.templateId,
            couponId: sendData.couponId,
            targetUsers: sendData.targetUsers,
        };
        handleSendCampaign(finalPayload);
    };

    const handleSendTestEmail = async (template) => {
        const recipientEmail = prompt(`Enter an email address to send a test of "${template.name}":`);
        if (!recipientEmail) return;
        try {
            const result = await fetchWithAuth(`${API_BASE_URL}/api/email-templates/send-test-email`, {
                method: 'POST',
                body: JSON.stringify({ templateAction: template.action, userEmail: recipientEmail }),
            });
            alert(result.message || `Test email sent successfully to ${recipientEmail}!`);
        } catch (err) {
            console.error("Failed to send test email:", err);
            alert(`Error: ${err.message}`);
        }
    };

    const handleScheduleClick = async (template) => {
        if (!template._id || template._id.startsWith('default-')) {
            alert("This template must be saved before it can be scheduled.");
            return;
        }
        const confirmationMessage = `This will schedule the email "${template.name}" to be sent to "${template.targetUsers}" in ${template.timeInterval}. Are you sure?`;
        if (!window.confirm(confirmationMessage)) return;
        try {
            const result = await fetchWithAuth(`${API_BASE_URL}/api/email-templates/schedule-email`, {
                method: 'POST',
                body: JSON.stringify({
                    templateId: template._id,
                    timeInterval: template.timeInterval,
                    targetUsers: template.targetUsers,
                }),
            });
            alert(result.message || 'Email scheduled successfully!');
            setIsEditModalOpen(false);
        } catch (err) {
            console.error("Failed to schedule email:", err);
            alert(`Error: ${err.message}`);
        }
    };

     const handleTimerIconClick = (templateAction) => {
        const currentTemplate = templates.find(t => t.action === templateAction) || createDefaultTemplate(templateAction);
        setTimerConfig({
            action: templateAction,
            label: actionOptions.find(opt => opt.value === templateAction)?.label,
            timeInterval: currentTemplate.timeInterval || '5 minutes',
        });
        setIsTimerModalOpen(true);
    };

    const handleSaveTimerConfig = async (config) => {
        setLoading(true);
        try {
            const result = await fetchWithAuth(`${API_BASE_URL}/api/email-templates/timers`, {
                method: 'POST',
                body: JSON.stringify({ action: config.action, timeInterval: config.timeInterval }),
            });
            alert(result.message || 'Timer rule saved successfully!');
            setIsTimerModalOpen(false);
        } catch (err) {
            console.error("Failed to save timer config:", err);
            alert(`Error: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    // --- RENDER LOGIC COMPONENTS ---
     const Sidebar = () => (
        <div className="emt-templates-sidebar">
            <h4>Template Types</h4>
            <ul className="emt-template-types-list">
                {actionOptions.map((option) => (
                    <li key={option.value} className={selectedTemplateType === option.value ? 'emt-active' : ''} onClick={() => setSelectedTemplateType(option.value)}>
                        <span className="emt-template-icon">{option.icon}</span>
                        <span>{option.label}</span>
                        {option.hasTimer && (<button className="emt-timer-btn" title={`Configure timer for ${option.label}`} onClick={(e) => { e.stopPropagation(); setTimerConfig({ action: option.value }); setIsTimerModalOpen(true); }}><Clock size={14} /></button>)}
                    </li>
                ))}
            </ul>
        </div>
    );

    const MultiTemplateView = ({ type }) => {
        const filteredTemplates = templates.filter(t => t.action === type.value);
        return (
            <div className="emt-multi-view-container">
                <div className="emt-template-header">
                    <h2>{type.label}</h2>
                    <button className="action-btn-primary" onClick={handleAddNewClick}><Plus size={16} /> Add New Template</button>
                </div>
                <div className="emt-template-grid">
                    {filteredTemplates.length > 0 ? (
                        filteredTemplates.map(template => (
                            <div key={template._id} className="emt-template-card">
                                <div className="emt-card-header">
                                    <h3>{template.name}</h3>
                                    <span className={`emt-status-badge ${template.isActive ? 'active' : 'inactive'}`}>{template.isActive ? 'Active' : 'Inactive'}</span>
                                </div>
                                <p className="emt-card-subject"><strong>Subject:</strong> {template.subject}</p>
                                <div className="emt-card-actions">
                                    {type.canSendManually && (<button className="action-btn-icon send-icon" title="Send as Campaign" onClick={() => handleManualSendClick(template)}><Send size={16} /></button>)}
                                    <button className="action-btn-icon" title="Preview" onClick={() => setPreviewingTemplate(template)}><Eye size={16} /></button>
                                    <button className="action-btn-icon" title="Edit" onClick={() => handleEditClick(template)}><Edit2 size={16} /></button>
                                    <button className="action-btn-danger-icon" title="Delete" onClick={() => handleDeleteTemplate(template._id)}><Trash2 size={16} /></button>
                                </div>
                            </div>
                        ))
                    ) : (
                        <div className="emt-no-templates-message">
                            <p>No templates have been created for this category yet.</p>
                            <button className="action-btn" onClick={handleAddNewClick}><Plus size={16} /> Create The First One</button>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    const SingleTemplateView = ({ type }) => {
        const [template, setTemplate] = useState(null);
        useEffect(() => {
            const found = templates.find(t => t.action === type.value) || createDefaultTemplate(type.value);
            setTemplate(found);
        }, [type, templates]);

        if (!template) return <div className="emt-loading-state">Loading...</div>;

        return (
            <div className="emt-template-preview-container">
                <div className="emt-template-header">
                    <h2>{template.name}</h2>
                    <div className="emt-template-actions">
                        {type.canSendManually && (<button className="action-btn-send" onClick={() => handleManualSendClick(template)}><Send size={16} /> Send</button>)}
                        <button className="action-btn" onClick={() => handleEditClick(template)}><Edit2 size={16} /> Edit</button>
                        {!template.isDefault && (<button className="action-btn-danger" onClick={() => handleDeleteTemplate(template._id)}><Trash2 size={16} /> Delete</button>)}
                    </div>
                </div>
                <div className="emt-template-details">
                    <div className="emt-preview-subject"><strong>Subject:</strong> {template.subject}</div>
                    <div className="emt-preview-content">
                        <iframe srcDoc={template.content} title="Template Preview" className="emt-preview-iframe" sandbox="allow-scripts" />
                    </div>
                </div>
            </div>
        );
    };


     const getPageTitle = () => {
        if (isEditModalOpen && editingTemplate) {
            return `${editingTemplate._id && !editingTemplate._id.startsWith('default-') ? 'Edit' : 'Create'} Template`;
        }
        if (previewingTemplate) {
            return `Preview: "${previewingTemplate.name}"`;
        }
        if (isSendModalOpen || isCouponModalOpen) {
            return 'Send Campaign';
        }
        if (isTimerModalOpen) {
            return 'Configure Timer';
        }
        const selectedOption = actionOptions.find(opt => opt.value === selectedTemplateType);
        return selectedOption ? selectedOption.label : 'Email Management';
    };

    const MainContentArea = () => {
        const selectedOption = actionOptions.find(opt => opt.value === selectedTemplateType);
         if (loading) {
        return (
            <>
                <Helmet><title>Loading Email Templates...</title></Helmet>
                <div className="emt-loading-state">Loading...</div>
            </>
        );
    }

    if (error) {
        return (
            <>
                <Helmet><title>Error - Email Management</title></Helmet>
                <div className="emt-error-state">{error}</div>
            </>
        );
    }

        if (!selectedOption) return <div className="emt-select-template-message">Select a template to begin.</div>;
        return selectedOption.isMultiTemplate ? <MultiTemplateView type={selectedOption} /> : <SingleTemplateView type={selectedOption} />;
    };

    return (
       <div className="emt-page">

        <Helmet>
                {/* This call will now work correctly */}
                <title>{`${getPageTitle()} | GN VISA Admin`}</title>
                <meta name="description" content="Create, manage, and send automated and manual email campaigns and templates." />
            </Helmet>

            <div className="emt-page-header">
                {/* <h1>Email Management</h1>
                <div className="emt-header-actions">
                    <button className="action-btn" onClick={() => setIsCouponModalOpen(true)}>
                        <Ticket size={16} /> Send Coupon Email
                    </button>
                    <Link to="/admin/list" className="action-btn">
                        <ListIcon size={16} /> Manage Lists
                    </Link>
                </div> */}
            </div>
            <div className="emt-content">
                <div className="emt-templates-with-sidebar">
                    <Sidebar />
                    <div className="emt-templates-main-content">
                        <MainContentArea />
                    </div>
                </div>
            </div>

            {/* --- NEW: Render the new SendCampaignModal --- */}
            <SendCampaignModal
                isOpen={isSendModalOpen}
                onClose={() => setIsSendModalOpen(false)}
                onSend={handleConfirmAndSend}
                coupons={availableCoupons}
                emailLists={emailLists}
            />

            {/* All Modals */}
             <CouponCampaignModal
                isOpen={isCouponModalOpen}
                onClose={() => setIsCouponModalOpen(false)}
                onSend={handleSendCampaign}
                templates={templates}
                coupons={availableCoupons}
            />
            {isEditModalOpen && (
                <EnhancedTemplateModal
                    isOpen={isEditModalOpen}
                    onClose={() => setIsEditModalOpen(false)}
                    onSave={handleSaveTemplate}
                    onManualSend={handleManualSendClick}
                    editingTemplate={editingTemplate}
                    onSendTest={handleSendTestEmail}
                    onSchedule={handleScheduleClick}
                    loading={loading}
                    actionOptions={actionOptions}
                    defaultTemplates={defaultTemplates}
                />
            )}
            {isTimerModalOpen && (
                <ConfigureTimerModal
                    open={isTimerModalOpen}
                    onClose={() => setIsTimerModalOpen(false)}
                    onSave={handleSaveTimerConfig} 
                    config={timerConfig}
                    loading={loading}
                />
            )}
           
            {previewingTemplate && (
                <div className="emt-preview-modal-overlay" onClick={() => setPreviewingTemplate(null)}>
                    <div className="emt-preview-modal-content" onClick={e => e.stopPropagation()}>
                        <div className="emt-preview-modal-header">
                            <h3>Preview: {previewingTemplate.name}</h3>
                            <button onClick={() => setPreviewingTemplate(null)} className="emt-close-btn"><X size={24} /></button>
                        </div>
                        <iframe srcDoc={previewingTemplate.content} title={`Preview of ${previewingTemplate.name}`} />
                    </div>
                </div>
            )}
        </div>
    );
};

export default EmailTemplate;